import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import "./BulkQuestionModal.css";

function BulkQuestionModal({ categories, onClose, onSubmit }) {
  const [questionsText, setQuestionsText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClose = () => {
    if (
      (submitting || questionsText.trim()) &&
      !window.confirm(
        submitting
          ? "Questions are still being added. Closing will not cancel the request. Close this modal?"
          : "You have unsaved questions. Discard them and close this modal?",
      )
    ) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const lines = questionsText.split("\n").filter((line) => line.trim());

    if (lines.length === 0) {
      setError("Please enter at least one question");
      return;
    }

    const questions = lines.map((line) => ({
      content: line.trim(),
      category_id: selectedCategory || undefined,
    }));

    setSubmitting(true);
    try {
      await onSubmit(questions);
      // A dismissed submission must not close a newly opened modal.
      if (mountedRef.current) onClose();
    } catch (err) {
      if (mountedRef.current) {
        setError(err.response?.data?.message || "Failed to add questions");
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const questionCount = questionsText
    .split("\n")
    .filter((line) => line.trim()).length;

  return (
    <Modal onClose={handleClose} title="Bulk Add Questions">
      <div className="bulk-question-modal">
        {error && <div className="error-message">{error}</div>}

        <form
          onSubmit={handleSubmit}
          id="bulk-question-form"
          name="bulk-question-form"
        >
          <div className="form-group">
            <label htmlFor="bulk-questions-text" className="form-label">
              Questions (one per line) *
            </label>
            <textarea
              id="bulk-questions-text"
              name="bulk-questions-text"
              className="textarea bulk-textarea"
              value={questionsText}
              onChange={(e) => setQuestionsText(e.target.value)}
              placeholder="What is your favorite color?&#10;How do you handle errors?&#10;What is the project timeline?"
              rows={10}
            />
            <div className="question-count">
              {questionCount} question{questionCount !== 1 ? "s" : ""} to add
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="bulk-category" className="form-label">
              Category (Optional)
            </label>
            <select
              id="bulk-category"
              name="bulk-category"
              className="input"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">No category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <p className="field-hint">
              All questions will be added to this category
            </p>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn"
              disabled={submitting}
              id="submit-bulk-questions"
              name="submit-bulk-questions"
            >
              {submitting && <span className="spinner"></span>}
              {submitting
                ? "Adding..."
                : `Add ${questionCount} Question${questionCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default BulkQuestionModal;
